import React, { useState } from 'react';

import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import StopCircleOutlinedIcon from '@mui/icons-material/StopCircleOutlined';
import { Box, ButtonBase, Collapse, Stack, Typography } from '@mui/material';

import type { LeDataUsageSummary } from '~/types/leData';

import { DUR_STATE, EASE_STATE, MONO_FONT_STACK } from '../chat/uiTokens';

import { type TimelineStep, type ToolStep, type WorkRow, businessWorkRows } from './projectTurn';

export type TurnStatus = 'live' | 'complete' | 'stopped' | 'failed';

const TABULAR = 'tabular-nums';
const ICON_SIZE = 14;
const INPUT_PREVIEW_CHARS = 240;

function formatStepDuration(ms: number): string {
  if (ms < 1000) {
    return `${Math.max(0, Math.round(ms))} ms`;
  }
  return `${(ms / 1000).toFixed(1)}s`;
}

export function formatStepSeconds(ms: number): string {
  return ms < 100 ? '<0.1s' : `${(ms / 1000).toFixed(1)}s`;
}

function formatUsage(usage: LeDataUsageSummary): string {
  return [
    `${usage.input.toLocaleString()} in`,
    `${usage.output.toLocaleString()} out`,
    `${(usage.cacheRead + usage.cacheWrite).toLocaleString()} cached`,
    `${usage.toolCalls} calls`,
    `$${usage.costUsd.toFixed(4)}`,
  ].join(' · ');
}

function compactInput(input: unknown): string | null {
  if (input === undefined || input === null) {
    return null;
  }
  const text = typeof input === 'string' ? input : JSON.stringify(input);
  if (!text || text === '{}') {
    return null;
  }
  return text.length > INPUT_PREVIEW_CHARS ? `${text.slice(0, INPUT_PREVIEW_CHARS)}…` : text;
}

function stepState(step: ToolStep | WorkRow, status: TurnStatus): ToolStep['state'] | 'stopped' {
  return step.state === 'running' && status !== 'live' ? 'stopped' : step.state;
}

function StepIcon({ state }: { state: ReturnType<typeof stepState> }) {
  if (state === 'complete') {
    return <CheckCircleOutlineIcon sx={{ fontSize: ICON_SIZE, color: 'text.disabled' }} />;
  }
  if (state === 'error') {
    return <ErrorOutlineRoundedIcon sx={{ fontSize: ICON_SIZE, color: 'error.main' }} />;
  }
  if (state === 'stopped') {
    return <StopCircleOutlinedIcon sx={{ fontSize: ICON_SIZE, color: 'text.disabled' }} />;
  }
  return <RadioButtonUncheckedIcon sx={{ fontSize: ICON_SIZE, color: 'primary.main' }} />;
}

const monoCaption = {
  fontFamily: MONO_FONT_STACK,
  fontSize: 11.5,
  color: 'text.disabled',
  fontVariantNumeric: TABULAR,
};

function formatWorkDuration(ms: number): string {
  const seconds = Math.max(1, Math.round(ms / 1000));
  return seconds < 60 ? `${seconds}s` : `${Math.floor(seconds / 60)}m ${seconds % 60}s`;
}

function workHeadline(status: TurnStatus, elapsedMs: number | undefined, stepCount: number): string {
  const duration = typeof elapsedMs === 'number' ? ` for ${formatWorkDuration(elapsedMs)}` : '';
  if (status === 'stopped') {
    return duration ? `Cancelled · ${formatWorkDuration(elapsedMs)}` : 'Cancelled';
  }
  if (duration) {
    return `Worked${duration}`;
  }
  if (stepCount === 0) {
    return 'Run details';
  }
  return `Worked through ${stepCount} ${stepCount === 1 ? 'step' : 'steps'}`;
}

function ToolRow({ step, status, debug }: { step: ToolStep; status: TurnStatus; debug: boolean }) {
  const state = stepState(step, status);
  const input = debug ? compactInput(step.input) : null;
  return (
    <Stack
      component="li"
      data-testid="work-step"
      data-state={state}
      data-tool={debug ? step.toolName : undefined}
      data-call-id={debug ? step.id : undefined}
      sx={{ py: 0.375 }}
    >
      <Stack direction="row" spacing={1} alignItems="center">
        <StepIcon state={state} />
        <Typography sx={{ fontSize: 13, color: 'text.secondary', flex: 1, minWidth: 0 }}>
          {step.label}
          {debug ? (
            <Box component="span" sx={{ ...monoCaption, ml: 0.75 }}>
              {step.toolName}
            </Box>
          ) : null}
        </Typography>
        {debug && typeof step.latencyMs === 'number' ? (
          <Typography data-testid="work-step-latency" sx={monoCaption}>
            {formatStepDuration(step.latencyMs)}
          </Typography>
        ) : null}
      </Stack>
      {debug ? (
        <Box sx={{ pl: 2.75 }}>
          <Typography sx={{ ...monoCaption, overflowWrap: 'anywhere' }}>{step.id}</Typography>
          {input ? (
            <Typography data-testid="work-step-input" title={input} sx={{ ...monoCaption, overflowWrap: 'anywhere' }}>
              {input}
            </Typography>
          ) : null}
        </Box>
      ) : null}
      {step.state === 'error' && step.errorText && debug ? (
        <Typography sx={{ pl: 2.75, fontSize: 12, color: 'error.main', overflowWrap: 'anywhere' }}>
          {step.errorText}
        </Typography>
      ) : null}
    </Stack>
  );
}

function BusinessRow({ row, status }: { row: WorkRow; status: TurnStatus }) {
  const state = stepState(row, status);
  return (
    <Stack
      component="li"
      direction="row"
      spacing={1}
      alignItems="center"
      data-testid="work-step"
      data-state={state}
      sx={{ py: 0.375 }}
    >
      <StepIcon state={state} />
      <Typography data-testid="work-step-label" sx={{ fontSize: 13, color: 'text.secondary', flex: 1, minWidth: 0 }}>
        {row.label}
      </Typography>
    </Stack>
  );
}

interface WorkSummaryProps {
  steps: ReadonlyArray<TimelineStep>;
  status: TurnStatus;
  debug: boolean;
  messageId: string;
  sessionId?: string;
  turnUsage?: LeDataUsageSummary;
  sessionUsage?: LeDataUsageSummary;
  elapsedMs?: number;
  inline?: boolean;
  method?: React.ReactNode;
}

const PILL_SX = { px: 1, py: 0.375, ml: -0.25, borderRadius: '8px', border: 1, borderColor: 'divider' };
const INLINE_SX = { px: 0.75, py: 0.5, ml: 0.5, borderRadius: '8px' };

export function useWorkSummary({
  steps,
  status,
  debug,
  messageId,
  sessionId,
  turnUsage,
  sessionUsage,
  elapsedMs,
  inline = false,
  method,
}: WorkSummaryProps): { toggle: React.ReactNode; details: React.ReactNode } {
  const [open, setOpen] = useState(false);
  const toolCount = steps.filter((step) => step.kind === 'tool').length;
  if (toolCount === 0 && !debug && elapsedMs === undefined && !method) {
    return { toggle: null, details: null };
  }
  const toggle = (
    <ButtonBase
      onClick={() => setOpen((prev) => !prev)}
      aria-expanded={open}
      data-testid="ui-stream-work"
      data-status={status}
      data-debug={debug || undefined}
      data-inline={inline || undefined}
      sx={{
        ...(inline ? INLINE_SX : PILL_SX),
        gap: 0.5,
        color: 'text.secondary',
        '&:hover': { color: 'text.primary', bgcolor: 'action.hover' },
      }}
    >
      <Typography data-testid="ui-stream-work-headline" sx={{ fontSize: 12.5, fontVariantNumeric: TABULAR }}>
        {workHeadline(status, elapsedMs, toolCount)}
      </Typography>
      <KeyboardArrowDownIcon
        sx={{
          fontSize: 16,
          transform: open ? 'rotate(180deg)' : 'none',
          transition: `transform ${DUR_STATE}ms ${EASE_STATE}`,
          '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
        }}
      />
    </ButtonBase>
  );
  const details = (
    <Collapse in={open} unmountOnExit>
      <Box
        component="ol"
        aria-label="What askRichard did"
        sx={{ listStyle: 'none', m: 0, mt: 0.75, pl: 1.5, ml: 0.75, borderLeft: 1, borderColor: 'divider' }}
      >
        {debug
          ? steps.map((step) =>
              step.kind === 'tool' ? (
                <ToolRow key={step.id} step={step} status={status} debug={debug} />
              ) : (
                <Typography
                  component="li"
                  key={step.id}
                  data-testid="work-thought"
                  sx={{ py: 0.375, pl: 2.75, fontSize: 12.5, fontStyle: 'italic', color: 'text.secondary' }}
                >
                  {step.text}
                </Typography>
              ),
            )
          : businessWorkRows(steps).map((row) => <BusinessRow key={row.id} row={row} status={status} />)}
      </Box>
      {method}
      {debug ? (
        <Stack spacing={0.25} sx={{ pt: 1, pl: 0.75 }}>
          {turnUsage ? (
            <Typography data-testid="ui-stream-debug-turn-usage" sx={monoCaption}>
              turn · {formatUsage(turnUsage)}
            </Typography>
          ) : null}
          {sessionUsage ? <Typography sx={monoCaption}>session · {formatUsage(sessionUsage)}</Typography> : null}
          <Typography sx={monoCaption}>
            message {messageId}
            {sessionId ? ` · session ${sessionId}` : ''}
          </Typography>
        </Stack>
      ) : null}
    </Collapse>
  );
  return { toggle, details };
}
